import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, FunnelIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { io } from 'socket.io-client';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatMoney } from '../utils/currency';
import { getToken } from '../utils/tokenStorage';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';

const Drivers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [currencyCode, setCurrencyCode] = useState('QAR');
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [isLiveConnected, setIsLiveConnected] = useState(false);
  const socketRef = useRef(null);
  const mapRef = useRef(null);
  const hasFittedBounds = useRef(false);
  const mapContainerStyle = useMemo(() => ({ width: '100%', height: '360px' }), []);
  const { isLoaded: isMapLoaded, loadError: mapLoadError, authFailed: mapAuthFailed } = useGoogleMaps();
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Car icon per vehicle type
  const carIcons = useMemo(() => {
    if (!isMapLoaded) return {};
    const base = process.env.PUBLIC_URL || '';
    const makeIcon = (path, w, h) => ({
      url: `${base}${path}`,
      scaledSize: new window.google.maps.Size(w, h),
      anchor: new window.google.maps.Point(w / 2, h / 2),
    });
    return {
      economy: makeIcon('/car-economy.svg', 28, 56),
      comfort: makeIcon('/car-comfort.svg', 28, 56),
      premium: makeIcon('/car-premium.svg', 30, 60),
      xl: makeIcon('/car-xl.svg', 32, 64),
    };
  }, [isMapLoaded]);

  const socketUrl = useMemo(() => {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';
    return apiBase.replace(/\/api\/?$/, '');
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [searchParams]);

  useEffect(() => {
    fetchCategoryOptions();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchDrivers({ silent: true });
    }, 10000);

    return () => clearInterval(interval);
  }, [searchParams]);

  useEffect(() => {
    const token = getToken();
    if (!token) return () => {};

    const socket = io(socketUrl, {
      transports: ['websocket'],
      auth: { token: `Bearer ${token}` },
      autoConnect: false,
    });
    socketRef.current = socket;

    const connectTimer = setTimeout(() => {
      socket.connect();
    }, 0);

    socket.on('connect', () => setIsLiveConnected(true));
    socket.on('disconnect', () => setIsLiveConnected(false));

    const handleDriverLocation = (payload = {}) => {
      if (!payload?.driverId || !payload?.location) return;

      setDrivers((prev) =>
        prev.map((driver) => {
          if (String(driver._id) !== String(payload.driverId)) return driver;

          return {
            ...driver,
            currentLocation: {
              type: 'Point',
              coordinates: [payload.location.longitude, payload.location.latitude],
            },
            lastLocationUpdatedAt: payload.timestamp || new Date().toISOString(),
          };
        })
      );
    };

    socket.on('driver:locationUpdated', handleDriverLocation);

    return () => {
      clearTimeout(connectTimer);
      socket.off('driver:locationUpdated', handleDriverLocation);
      socket.disconnect();
    };
  }, [socketUrl]);

  const fetchDrivers = async ({ silent = false } = {}) => {
    try {
      const page = searchParams.get('page') || 1;
      const status = searchParams.get('status') || '';
      const searchValue = searchParams.get('search') || '';
      const params = new URLSearchParams({
        page,
        limit: 10,
        ...(searchValue && { search: searchValue }),
        ...(status && { status })
      });

      const response = await api.get(`/admin/drivers?${params}`);
      setDrivers(response.data.data);
      setPagination(response.data.pagination);
      setCurrencyCode(String(response.data?.currencyCode || 'QAR').toUpperCase());
    } catch (error) {
      if (!silent) {
        toast.error('Failed to fetch drivers');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchCategoryOptions = async () => {
    try {
      const response = await api.get('/admin/ride-types');
      const options = (response?.data?.data?.rideTypes || [])
        .filter((type) => type?.key)
        .map((type) => ({ value: type.key, label: type.label || type.key }));
      setCategoryOptions(options);
    } catch (error) {
      toast.error('Failed to load car categories');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleApprove = async (driverId) => {
    try {
      await api.put(`/admin/drivers/${driverId}/approve`);
      toast.success('Driver approved successfully');
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve driver');
    }
  };

  const handleStatusChange = async (driverId, newStatus) => {
    try {
      await api.put(`/admin/drivers/${driverId}/status`, { status: newStatus });
      toast.success('Driver status updated');
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteDriver = (driverId, fullName) => {
    setDeleteTarget({ id: driverId, name: fullName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      await api.delete(`/admin/drivers/${deleteTarget.id}`);
      toast.success('Driver deleted successfully');
      setDeleteTarget(null);
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete driver');
    }
  };

  const handleVehicleTypeChange = async (driverId, vehicleType) => {
    try {
      await api.put(`/admin/drivers/${driverId}/vehicle-type`, { vehicleType });
      setDrivers((prev) =>
        prev.map((driver) =>
          String(driver._id) === String(driverId)
            ? {
                ...driver,
                vehicle: {
                  ...driver.vehicle,
                  vehicleType,
                },
              }
            : driver
        )
      );
      toast.success('Category updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update category');
    }
  };

  const formatLocation = (driver) => {
    const coords = driver?.currentLocation?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return 'N/A';

    const longitude = Number(coords[0]);
    const latitude = Number(coords[1]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return 'N/A';

    return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
  };

  const getDriverLatLng = (driver) => {
    const coords = driver?.currentLocation?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return null;

    const longitude = Number(coords[0]);
    const latitude = Number(coords[1]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (latitude === 0 && longitude === 0) return null;

    return { lat: latitude, lng: longitude };
  };

  const trackedDrivers = useMemo(
    () => drivers.filter((driver) => driver.isOnline && getDriverLatLng(driver)),
    [drivers]
  );

  // Only compute center once (Doha default); map stays fixed unless user searches
  const DOHA_CENTER = useMemo(() => ({ lat: 25.2854, lng: 51.531 }), []);

  const isSearchActive = Boolean(searchParams.get('search'));

  // When searching for a specific driver, center on their location
  const mapCenter = useMemo(() => {
    if (isSearchActive && trackedDrivers.length === 1) {
      return getDriverLatLng(trackedDrivers[0]) || DOHA_CENTER;
    }
    return DOHA_CENTER;
  }, [isSearchActive, trackedDrivers.length <= 1 ? trackedDrivers : null, DOHA_CENTER]);

  // Fit map bounds to show all tracked drivers (only on initial load or filter change)
  const fitMapToDrivers = useCallback(() => {
    if (!mapRef.current || trackedDrivers.length === 0) return;
    // When searching for one driver, don't fit bounds — just center+zoom
    if (isSearchActive && trackedDrivers.length === 1) {
      mapRef.current.setZoom(15);
      return;
    }
    if (trackedDrivers.length === 1) {
      mapRef.current.setZoom(13);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    trackedDrivers.forEach((driver) => {
      const pos = getDriverLatLng(driver);
      if (pos) bounds.extend(pos);
    });
    mapRef.current.fitBounds(bounds, 50);
  }, [trackedDrivers.length, isSearchActive]);

  // Fit bounds once when drivers first load, and when search/filter changes
  useEffect(() => {
    if (trackedDrivers.length > 0 && mapRef.current) {
      if (!hasFittedBounds.current || isSearchActive) {
        fitMapToDrivers();
        hasFittedBounds.current = true;
      }
    }
  }, [searchParams, fitMapToDrivers, trackedDrivers.length]);

  const formatLastSeen = (value) => {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';

    const diffMs = Date.now() - date.getTime();
    const seconds = Math.max(0, Math.floor(diffMs / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleString();
  };

  const getLastSeenValue = (driver) => {
    return driver?.lastLocationUpdatedAt || driver?.updatedAt || driver?.createdAt || null;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Drivers</h1>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              isLiveConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full mr-2 ${isLiveConnected ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            {isLiveConnected ? 'Live tracking connected' : 'Live tracking disconnected'}
          </span>
          <span className="text-gray-500">{pagination.total} total drivers</span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, or ID number..."
              className="input pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input sm:w-40"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <button type="submit" className="btn-primary">
            <FunnelIcon className="w-5 h-5 mr-2 inline" />
            Filter
          </button>
        </form>
      </div>

      {/* Live Driver Tracking Map */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Live Driver Tracking (Google Maps)</h2>
          <span className="text-sm text-gray-500">{trackedDrivers.length} online with location</span>
        </div>

        {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Set <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> in <strong>admin-dashboard/.env</strong> and restart admin dashboard to enable map tracking.
          </div>
        ) : mapLoadError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Google Maps failed to load. Ensure <strong>Maps JavaScript API</strong> is enabled for this key in Google Cloud and that billing/restrictions allow localhost.
          </div>
        ) : mapAuthFailed ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Google Maps rejected this API key (e.g. <code>ProjectDeniedMapError</code>). Confirm the key’s <strong>API restrictions</strong> include <strong>Maps JavaScript API</strong>, the API is enabled in the key’s project, and billing is active. Restart the dashboard after changing the key.
          </div>
        ) : !isMapLoaded ? (
          <div className="h-[360px] flex items-center justify-center text-gray-500">Loading map...</div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={12}
            onLoad={(map) => { mapRef.current = map; }}
            onUnmount={() => { mapRef.current = null; }}
            options={{
              fullscreenControl: false,
              streetViewControl: false,
              mapTypeControl: false,
              gestureHandling: 'greedy',
            }}
          >
            {trackedDrivers.map((driver) => {
              const position = getDriverLatLng(driver);
              if (!position) return null;
              const vType = driver.vehicle?.vehicleType || 'economy';
              return (
                <MarkerF
                  key={driver._id}
                  position={position}
                  icon={carIcons[vType] || carIcons.economy}
                  title={`${driver.firstName || ''} ${driver.lastName || ''} (${vType})`.trim()}
                />
              );
            })}
          </GoogleMap>
        )}
      </div>

      {/* Drivers Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Driver</th>
                <th className="table-header">Vehicle</th>
                <th className="table-header">Rating</th>
                <th className="table-header">Rides</th>
                <th className="table-header">Earnings</th>
                <th className="table-header">Status</th>
                <th className="table-header">Email Verified</th>
                <th className="table-header">Current Location</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {drivers.map((driver) => (
                <tr key={driver._id} className="hover:bg-gray-50">
                  <td className="table-cell">
                    <div className="flex items-center">
                      <div className="relative">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-green-600 font-medium">
                            {driver.firstName?.[0]}{driver.lastName?.[0]}
                          </span>
                        </div>
                        {driver.isOnline && (
                          <span className="absolute bottom-0 right-2 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{driver.firstName} {driver.lastName}</p>
                        <p className="text-xs text-gray-500">{driver.email}</p>
                        {driver.employeeNumber && (
                          <p className="text-xs text-gray-400">ID #{driver.employeeNumber}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <p className="font-medium">{driver.vehicle?.make} {driver.vehicle?.model}</p>
                    <p className="text-xs text-gray-500">{driver.vehicle?.licensePlate}</p>
                    <p className="text-xs text-indigo-600 capitalize">{driver.vehicle?.vehicleType || 'economy'}</p>
                  </td>
                  <td className="table-cell">
                    <span className="text-yellow-500">★</span> {driver.rating?.toFixed(1) || '5.0'}
                  </td>
                  <td className="table-cell">{driver.totalRides || 0}</td>
                  <td className="table-cell">{formatMoney(driver.totalEarnings || 0, currencyCode || 'QAR')}</td>
                  <td className="table-cell"><StatusBadge status={driver.status} /></td>
                  <td className="table-cell">
                    <span className={`status-badge ${driver.emailVerified ? 'status-active' : 'status-pending'}`}>
                      {driver.emailVerified ? 'Verified' : 'Not verified'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <p className="font-medium">{formatLocation(driver)}</p>
                    <p className="text-xs text-gray-500">Updated {formatLastSeen(getLastSeenValue(driver))}</p>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/drivers/${driver._id}`}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        View
                      </Link>
                      {driver.status === 'pending' ? (
                        <button
                          onClick={() => handleApprove(driver._id)}
                          className="flex items-center text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Approve
                        </button>
                      ) : (
                        <select
                          value={driver.status}
                          onChange={(e) => handleStatusChange(driver._id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="suspended">Suspended</option>
                        </select>
                      )}
                      <select
                        value={driver.vehicle?.vehicleType || 'economy'}
                        onChange={(e) => handleVehicleTypeChange(driver._id, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        {categoryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleDeleteDriver(driver._id, `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || 'this driver')}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          page={pagination.page}
          pages={pagination.pages}
          onPageChange={(p) => setSearchParams(prev => { prev.set('page', p); return prev; })}
        />
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Driver"
        message={`Delete driver account for ${deleteTarget?.name}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default Drivers;
